import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../environments/environment';

/**
 * Sentry Service
 * 
 * Handles Sentry error tracking configuration and user context management.
 * Includes PII scrubbing, performance tracing, and user identification.
 */
@Injectable({
  providedIn: 'root'
})
export class SentryService {
  
  /**
   * Initialize Sentry with configuration from environment.
   * Should be called before Angular bootstraps.
   */
  static initializeSentry(): void {
    const sentryConfig = (environment as any).sentry;
    
    if (!sentryConfig?.enabled) {
      console.log('Sentry is disabled for this environment');
      return;
    }

    Sentry.init({
      dsn: sentryConfig.dsn,
      environment: sentryConfig.name,
      
      // Release tracking - should be set during CI/CD build
      release: `miyar-dashboard@${(globalThis as any).APP_VERSION || '1.0.0'}`,
      
      // Performance Monitoring
      tracesSampleRate: sentryConfig.tracesSampleRate || 0.1,
      
      // Session Replay
      replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate || 0.1,
      replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate || 1,
      
      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      
      // Enable distributed tracing
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/.*\.miyar\.(dev|uk|com\.sa)/,
      ],
      
      // BeforeSend hook for PII scrubbing
      beforeSend(event, hint) {
        return SentryService.scrubPII(event) as Sentry.ErrorEvent;
      },
      
      // BeforeSendTransaction for performance events
      beforeSendTransaction(event) {
        // Cast to return type expected by Sentry
        return SentryService.scrubPII(event) as typeof event;
      },
    });
    
    console.log(`Sentry initialized for environment: ${sentryConfig.name}`);
  }
  
  /**
   * Scrub PII (Personally Identifiable Information) from Sentry events.
   * Removes sensitive data like tokens, passwords, national IDs, etc.
   */
  static scrubPII(event: Sentry.Event): Sentry.Event {
    // List of sensitive field names to scrub
    const sensitiveFields = [
      'password',
      'token',
      'accessToken',
      'access_token',
      'refreshToken',
      'refresh_token',
      'authorization',
      'nationalId',
      'national_id',
      'idNumber',
      'phoneNumber',
      'phone_number',
      'email',
      'birthDate',
      'birth_date',
      'creditCard',
      'credit_card',
      'cvv',
      'ssn',
      'apiKey',
      'api_key',
      'secret',
      'privateKey',
      'private_key',
    ];
    
    // Regex patterns for PII detection
    const jwtPattern = /^eyJ[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*$/;
    const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
    const phonePattern = /(\+966|05)\d{8,9}/;
    const nationalIdPattern = /^\d{10}$/;
    
    const scrubStringValue = (value: string): string => {
      if (jwtPattern.test(value)) return '[FILTERED_TOKEN]';
      if (emailPattern.test(value)) return '[FILTERED_EMAIL]';
      if (phonePattern.test(value)) return '[FILTERED_PHONE]';
      if (nationalIdPattern.test(value)) return '[FILTERED_ID]';
      return value;
    };
    
    const scrubValue = (obj: any, depth = 0): any => {
      if (depth > 10 || !obj) return obj;
      
      if (typeof obj === 'string') {
        return scrubStringValue(obj);
      }
      
      if (Array.isArray(obj)) {
        return obj.map(item => scrubValue(item, depth + 1));
      }
      
      if (typeof obj === 'object') {
        const scrubbed: any = {};
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            const lowerKey = key.toLowerCase();
            if (sensitiveFields.some(field => lowerKey.includes(field.toLowerCase()))) {
              scrubbed[key] = '[FILTERED]';
            } else {
              scrubbed[key] = scrubValue(obj[key], depth + 1);
            }
          }
        }
        return scrubbed;
      }
      
      return obj;
    };
    
    // Scrub request data
    if (event.request) {
      if (event.request.headers) {
        event.request.headers = scrubValue(event.request.headers);
      }
      if (event.request.data) {
        event.request.data = scrubValue(event.request.data);
      }
      if (event.request.cookies) {
        delete event.request.cookies;
      }
    }
    
    // Scrub breadcrumbs
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map(breadcrumb => ({
        ...breadcrumb,
        data: scrubValue(breadcrumb.data),
      }));
    }
    
    // Scrub extra data
    if (event.extra) {
      event.extra = scrubValue(event.extra);
    }
    
    // Scrub context data
    if (event.contexts) {
      event.contexts = scrubValue(event.contexts);
    }
    
    return event;
  }
  
  /**
   * Set user context for Sentry.
   * Call this after user login.
   */
  setUserContext(user: { userId?: string; email?: string; userRoleId?: any }): void {
    if (!user) {
      Sentry.setUser(null);
      return;
    }
    
    Sentry.setUser({
      id: user.userId,
      // Don't send email to Sentry for privacy
      // email: user.email,
    });
    
    // Set additional context
    Sentry.setTag('userRoleId', user.userRoleId?.toString() || 'unknown');
  }
  
  /**
   * Clear user context on logout.
   */
  clearUserContext(): void {
    Sentry.setUser(null);
  }
  
  /**
   * Set correlation ID for distributed tracing.
   */
  setCorrelationId(correlationId: string): void {
    Sentry.setTag('correlationId', correlationId);
  }
  
  /**
   * Add custom context tags.
   */
  setTag(key: string, value: string): void {
    Sentry.setTag(key, value);
  }
  
  /**
   * Add extra context data.
   */
  setExtra(key: string, value: any): void {
    Sentry.setExtra(key, value);
  }
  
  /**
   * Capture an exception manually.
   */
  captureException(error: Error, context?: Record<string, any>): void {
    if (context) {
      Sentry.setExtras(context);
    }
    Sentry.captureException(error);
  }
  
  /**
   * Capture a message manually.
   */
  captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): void {
    Sentry.captureMessage(message, level);
  }
  
  /**
   * Add a breadcrumb for debugging.
   */
  addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
    Sentry.addBreadcrumb(breadcrumb);
  }
}
